import React, { useState, useEffect, useRef } from 'react';
import {
    Box,
    Button,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalCloseButton,
    ModalBody,
    ModalFooter,
    FormControl,
    FormLabel,
    Select,
    VStack,
    Text,
    useDisclosure,
    useToast,
    Input,
    HStack,
    Divider,
    Grid,
    Flex,
    Spinner,
} from '@chakra-ui/react';
import fetchWrapper from '../lib/fetch-wrapper';
import { WarningIcon, RepeatIcon } from '@chakra-ui/icons';
import { AccountInfo, IPublicClientApplication } from '@azure/msal-browser';
import { useMsal } from '@azure/msal-react';
import { useProfile } from '../lib/profileContext';
import FileDropZone from '../components/FileDropZone';
import MultiSelect from '../components/multiSelect';
import { MdUploadFile } from 'react-icons/md';
import FilesTable from '../components/fileUploadComponents/filesTable';
import ReplaceModal from '../components/fileUploadComponents/replaceModal';
import ConfirmReplaceModal from '../components/fileUploadComponents/confirmReplaceModal';
import FileUploadStepper from '../components/fileUploadComponents/fileUploadStepper';
import { topicsOptions, languagesOptions } from '../lib/constants';
import DeleteConfirmationModal from '../components/fileUploadComponents/confirmDeleteModal';
import PiiDataViewer from '../components/fileUploadComponents/piiDataViewer';
import PiiDocumentModal from '../components/fileUploadComponents/PiiDocumentModal';

const FilesPage = () => {
    const { instance, accounts }: { instance: IPublicClientApplication; accounts: AccountInfo[] } = useMsal();
    const { profile } = useProfile();
    const [files, setFiles] = useState<any[]>([]);
    const filesRef = useRef<any[]>([]);
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [fileName, setFileName] = useState('');
    const [matchingFiles, setMatchingFiles] = useState<any[]>([]);
    const [topic, setTopic] = useState('');
    const [language, setLanguage] = useState('');
    const [properties, setProperties] = useState<string[]>([]);
    const { isOpen: isUploadOpen, onOpen: onUploadOpen, onClose: onUploadClose } = useDisclosure();
    const { isOpen: isReplaceOpen, onOpen: onReplaceOpen, onClose: onReplaceClose } = useDisclosure();
    const toast = useToast();
    const [activeStep, setActiveStep] = useState(0);
    const [isReplaceConfirmOpen, setIsReplaceConfirmOpen] = useState(false);
    const [fileToReplace, setFileToReplace] = useState<any | null>(null);
    const [fileToDelete, setFileToDelete] = useState<any | null>(null);
    const [isCheckCompleted, setIsCheckCompleted] = useState(true);
    const [isUploadingFinalFile, setIsUploadingFinalFile] = useState(false);
    const [propertyOptions, setPropertyOptions] = useState<{ value: string; label: string }[]>([]);
    const { isOpen: isDeleteModalOpen, onOpen: onDeleteModalOpen, onClose: onDeleteModalClose } = useDisclosure();
    const [piiData, setPiiData] = useState<{ detectedText: string; category: string; page: string; }[]>([]);
    const [isPiiConfirmed, setIsPiiConfirmed] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [selectedEntity, setSelectedEntity] = useState<any | null>(null);
    const [fullResults, setFullResults] = useState<any[]>([]);
    const { isOpen: isDocModalOpen, onOpen: onDocModalOpen, onClose: onDocModalClose } = useDisclosure();
    const intervalRef = useRef<any>(null);

    const steps = ['Upload File', 'Enter Properties'];

    const resetForm = () => {
        setSelectedFile(null);
        setFileName('');
        setTopic('');
        setLanguage('');
        setProperties([]);
        setMatchingFiles([]);
        setActiveStep(0);
        setPiiData([]);
        setIsPiiConfirmed(false);
    };

    const fetchFiles = async () => {
        const fetchInstance = new fetchWrapper();
        try {
            const response = await fetchInstance.getFiles('/api/files');
            setFiles(response.data || []);
        } catch (error) {
            console.error('Error fetching files:', error);
            toast({
                title: 'Error',
                description: 'Failed to load files',
                status: 'error',
                duration: 3000,
                isClosable: true,
            });
        }
    };

    const handleFileNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setFileName(e.target.value);
    };

    const handlePiiClick = (entity: any) => {
        setSelectedEntity(entity);
        setTimeout(onDocModalOpen, 100);
    };

    const fetchMatchingFiles = async (name: string) => {
        try {
            const fetchInstance = new fetchWrapper();
            const response = await fetchInstance.searchFilesByName('/api/files/search', name);
            setMatchingFiles(response.data || []);
        } catch (error) {
            console.error('Error fetching matching files:', error);
        }
    };

    const handleDeleteFile = (file: any) => {
        setFileToDelete(file);
        onDeleteModalOpen();
    };

    const handleFileSelect = async (file: File | null) => {
        setSelectedFile(file);
        setIsPiiConfirmed(false);
        setPiiData([]);
        setIsCheckCompleted(false);

            if (file) {
                const nameWithoutExtension = file.name.replace(/\.[^/.]+$/, '');
                setFileName(nameWithoutExtension);

                setIsUploading(true);
                try {
                    const fetchInstance = new fetchWrapper();
                    const { flatPiiData, rawResults } = await fetchInstance.checkPiiFile('/api/files/check', file);
                    setPiiData(flatPiiData);
                    setFullResults(rawResults);
                    } catch (error) {
                         console.error('Error checking PII:', error);
                         toast({
                             title: 'Error',
                             description: 'Failed to check file for PII data.',
                             status: 'error',
                             duration: 3000,
                             isClosable: true,
                        });
                    } finally {
                        setIsUploading(false);
                        setIsCheckCompleted(true);
                    }
                } else {
                  setIsCheckCompleted(true);
                }
    };

    const handleNextStep = () => {
        if (activeStep === 0 && (!selectedFile || !fileName)) {
            toast({
                title: 'Missing Details',
                description: 'Please select a file and enter a name.',
                status: 'warning',
                duration: 3000,
                isClosable: true,
            });
            return;
        }
        setActiveStep((prevStep) => prevStep + 1);
    };

    const handleUpload = async () => {
        if (!language || !topic || properties.length === 0 || !fileName) {
            toast({
                title: 'Missing Details',
                description: 'Please fill out all fields in Step 2.',
                status: 'warning',
                duration: 3000,
                isClosable: true,
            });
            return;
        }

        setIsUploadingFinalFile(true);
        try {
            const originalExtension = selectedFile!.name.split('.').pop() || '';
            const baseFileName = fileName.replace(/\.[^/.]+$/, '');
            const sanitizedFileName = `${baseFileName}.${originalExtension}`;

            const renamedFile = new File([selectedFile!], sanitizedFileName, {
                type: selectedFile!.type,
            });

            const formData = {
                fileName: baseFileName,
                language,
                topic,
                properties,
            };

            const fetchInstance = new fetchWrapper();
            await fetchInstance.sendFileData('/api/upload', renamedFile, formData);

            toast({
                title: 'Success',
                description: 'File uploaded successfully!',
                status: 'success',
                duration: 3000,
                isClosable: true,
            });

            resetForm();
            onUploadClose();
            void fetchFiles();
        } catch (error) {
            console.error('Error uploading file:', error);
            toast({
                title: 'Error',
                description: 'Failed to upload file.',
                status: 'error',
                duration: 3000,
                isClosable: true,
            });
        } finally {
            setIsUploadingFinalFile(false); // Stop spinner
        }
    };

    const confirmDeleteFile = async () => {
        if (!fileToDelete) return;

        try {
            const fetchInstance = new fetchWrapper();
            await fetchInstance.deleteFile('/api/files/delete', {
                PartitionKey: fileToDelete.PartitionKey,
                RowKey: fileToDelete.RowKey,
                FileName: fileToDelete.FileName,
                FileUrl: fileToDelete.FileUrl,
            });

            toast({
                title: 'Success',
                description: `File '${fileToDelete.FileName}' deleted successfully!`,
                status: 'success',
                duration: 3000,
                isClosable: true,
            });

            setFileToDelete(null);
            onDeleteModalClose();
            void fetchFiles();
        } catch (error) {
            console.error('Error deleting file:', error);
            toast({
                title: 'Error',
                description: 'Failed to delete file.',
                status: 'error',
                duration: 3000,
                isClosable: true,
            });
        }
    };

    const handleReplaceFile = (file: any) => {
        setFileToReplace(file);
        setIsReplaceConfirmOpen(true);
    };

    const handleReplace = (file: any) => {
        setFileToReplace(file);
        onReplaceOpen();
    };

    const confirmReplaceFile = async () => {
        if (!selectedFile || !fileToReplace) return;

        const replaceData = {
            RowKey: fileToReplace.RowKey,
            PartitionKey: fileToReplace.PartitionKey,
        };

        const formData = new FormData();
        formData.append('file', selectedFile!);
        formData.append('data', JSON.stringify(replaceData));

        try {
            const fetchInstance = new fetchWrapper();
            await fetchInstance.replaceFile('/api/replace', selectedFile, replaceData);
            toast({
                title: 'Success',
                description: `File '${fileToReplace.FileName}' replaced successfully!`,
                status: 'success',
                duration: 3000,
                isClosable: true,
            });
            resetForm();
            setIsReplaceConfirmOpen(false);
            onUploadClose();
            onReplaceClose();
            void fetchFiles();
        } catch (error) {
            console.error('Error replacing file:', error);
            toast({
                title: 'Error',
                description: 'Failed to replace file.',
                status: 'error',
                duration: 3000,
                isClosable: true,
            });
        }
    };

    const handleReplaceModalClose = () => {
        resetForm();
        onReplaceClose();
    };

    const handlePropertiesChange = (selectedOptions: any[]) => {
        const selectedValues = selectedOptions.map((option) => option.value);

        if (selectedValues.includes('ALL')) {
            setProperties(['ALL']);
        } else {
            setProperties(selectedValues.filter((value) => value !== 'ALL'));
        }
    };

    const fetchFilesWrapper = async () => {
        await fetchFiles();

        if (intervalRef.current) {
            clearInterval(intervalRef.current);
        }

        intervalRef.current = setInterval(() => {
            if (filesRef.current.some((item) => item.Status === 'In Progress' || item.Status === 'Deleting')) {
                void fetchFiles();
            }
        }, 7000);
    };

    const fetchProperties = async () => {
        try {
            const fetchInstance = new fetchWrapper();
            const response: any = await fetchInstance.getProperties();
            const formatted = response.data.records
                .map((record: any) => ({
                    value: record.data.Name,
                    label: record.data.Name,
                }))
                .sort((a: any, b: any) => a.label.localeCompare(b.label));
            setPropertyOptions(formatted);
        } catch (error) {
            toast({
                title: 'Error',
                description: 'Failed to fetch property options.',
                status: 'error',
                duration: 3000,
                isClosable: true,
            });
        }
    };

    useEffect(() => {
        filesRef.current = files;
    }, [files]);

    useEffect(() => {
        void fetchProperties();
        void fetchFilesWrapper();

        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
            }
        };
    }, []);

    useEffect(() => {
        const trimmedName = fileName.trim();

        if (!trimmedName) {
            setMatchingFiles([]);
            return;
        }

        const handler = setTimeout(() => {
            void fetchMatchingFiles(trimmedName);
        }, 500);

        return () => clearTimeout(handler);
    }, [fileName]);

    return (
        <Box mx="auto" mt="6" bg="#ffffff" height="100%" m="0" px="30px" pt="30px" overflowY="auto">
            <Button
                variant="brand"
                size="lg"
                height="4rem"
                leftIcon={<MdUploadFile size="1.5em" />}
                onClick={() => {
                    resetForm();
                    onUploadOpen();
                }}
            >
                Upload File
            </Button>

            <Text fontSize="2xl" fontWeight="bold" mt="40px">
                All Files
            </Text>
            <FilesTable
                files={files}
                propertyOptions={propertyOptions}
                onReplace={handleReplace}
                onDelete={handleDeleteFile}
                onUpdateProperties={(rowKey: string, updatedProperties: string[]) => {
                    setFiles(prev =>
                        prev.map(file =>
                            file.RowKey === rowKey
                                ? { ...file, Property: updatedProperties.join(',') }
                                : file
                        )
                    );
                }}
            />

            <Modal
                isOpen={isUploadOpen}
                closeOnOverlayClick={false}
                onClose={() => {
                    resetForm();
                    onUploadClose();
                }}
                blockScrollOnMount={false}
                size="2xl"
            >
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader>Upload File</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        <FileUploadStepper activeStep={activeStep} steps={steps} />

                        {activeStep === 0 && (
                            <VStack spacing={4} align="stretch">
                                <FileDropZone onFileSelect={handleFileSelect} selectedFile={selectedFile} />
                                {isUploading && (
                                    <Flex alignItems="center">
                                        <Spinner size="sm" mr={2} color="blue.500" />
                                        <Text fontWeight="bold" color="blue.600">
                                            Uploading file for PII verification. Please wait...
                                        </Text>
                                    </Flex>
                                )}

                                {piiData.length > 0 && (
                                    <PiiDataViewer
                                        piiData={piiData}
                                        isPiiConfirmed={isPiiConfirmed}
                                        togglePiiConfirmed={() => setIsPiiConfirmed((prev) => !prev)}
                                        onPiiClick={handlePiiClick}
                                    />
                                )}

                                {selectedFile && (
                                    <FormControl id="fileName" isRequired>
                                        <FormLabel>File Name</FormLabel>
                                        <Input
                                            placeholder="Enter File Name"
                                            value={fileName}
                                            onChange={handleFileNameChange}
                                        />
                                        {matchingFiles.length > 0 && (
                                            <Box bg="#fdf6df" pt={4} px={0} borderRadius="md">
                                                <HStack mb={2} ml={4}>
                                                    <WarningIcon color="yellow.500" />
                                                    <Text fontWeight="bold">
                                                        Existing Files with this Name ({matchingFiles.length})
                                                    </Text>
                                                </HStack>
                                                {matchingFiles.map((file, index) => (
                                                    <Box
                                                        key={index}
                                                        mb={index < matchingFiles.length - 1 ? 4 : 0}
                                                        p={4}
                                                        bg="#fdf6df"
                                                        borderRadius="md"
                                                    >
                                                        <Grid templateColumns="120px auto" gap={2} alignItems="center">
                                                            <Text fontWeight="bold">File Name</Text>
                                                            <Text>{file.FileName}</Text>

                                                            <Text fontWeight="bold">Topic</Text>
                                                            <Text>{file.Topic}</Text>

                                                            <Text fontWeight="bold">Property</Text>
                                                            <Text overflow="auto">{file.Property}</Text>

                                                            <Text fontWeight="bold">Language</Text>
                                                            <HStack justifyContent="space-between" w="100%">
                                                                <Text>{file.Language}</Text>
                                                                <Button
                                                                    size="sm"
                                                                    leftIcon={<RepeatIcon />}
                                                                    colorScheme="green"
                                                                    variant="ghost"
                                                                    onClick={() => handleReplaceFile(file)}
                                                                >
                                                                    Replace
                                                                </Button>
                                                            </HStack>
                                                        </Grid>
                                                        {index < matchingFiles.length - 1 && <Divider borderColor="#91979e" mt={4} />}
                                                    </Box>
                                                ))}
                                            </Box>
                                        )}
                                    </FormControl>
                                )}
                            </VStack>
                        )}

                        {activeStep === 1 && (
                            <VStack spacing={6} align="stretch">
                                <FormControl id="topic" isRequired>
                                    <FormLabel>Topic</FormLabel>
                                    <Select
                                        placeholder="Select a Topic"
                                        value={topic}
                                        onChange={(e) => setTopic(e.target.value)}
                                    >
                                        {topicsOptions.map((item: string) => (
                                            <option key={item} value={item}>{item}</option>
                                        ))}
                                    </Select>
                                </FormControl>

                                <FormControl id="properties" isRequired>
                                    <FormLabel>Properties</FormLabel>
                                    <MultiSelect
                                        options={[
                                            { value: 'ALL', label: 'ALL' },
                                            ...propertyOptions
                                        ]}
                                        value={properties.map((prop) => ({ value: prop, label: prop }))}
                                        onChange={handlePropertiesChange}
                                    />
                                </FormControl>

                                <FormControl id="language" isRequired>
                                    <FormLabel>Language</FormLabel>
                                    <Select
                                        placeholder="Select Language"
                                        value={language}
                                        onChange={(e) => setLanguage(e.target.value)}
                                    >
                                        {languagesOptions.map((item: string) => (
                                            <option key={item} value={item}>{item}</option>
                                        ))}
                                    </Select>
                                </FormControl>
                            </VStack>
                        )}
                    </ModalBody>
                    <ModalFooter>
                        {activeStep > 0 && (
                            <Button variant="cancel" onClick={() => setActiveStep((prevStep) => prevStep - 1)} mr="3">
                                Back
                            </Button>
                        )}
                        {activeStep === steps.length - 1 ? (
                            <Button
                                variant="brand"
                                onClick={handleUpload}
                                isLoading={isUploadingFinalFile}
                                isDisabled={!language || !topic || properties.length === 0 || isUploadingFinalFile}
                            >
                                Upload
                            </Button>
                        ) : (
                            <Button
                                variant="brand"
                                onClick={handleNextStep}
                                isDisabled={!selectedFile || !selectedFile || (!!piiData.length && !isPiiConfirmed) || !isCheckCompleted}
                            >
                                Next
                            </Button>
                        )}
                    </ModalFooter>
                </ModalContent>
            </Modal>

            <ConfirmReplaceModal
                isOpen={isReplaceConfirmOpen}
                onClose={() => setIsReplaceConfirmOpen(false)}
                fileToReplace={fileToReplace}
                onConfirmReplace={confirmReplaceFile}
            />

            <ReplaceModal
                isOpen={isReplaceOpen}
                onClose={handleReplaceModalClose}
                onFileSelect={handleFileSelect}
                selectedFile={selectedFile}
                onUpload={confirmReplaceFile}
            />

            <DeleteConfirmationModal
                isOpen={isDeleteModalOpen}
                onClose={onDeleteModalClose}
                fileName={fileToDelete?.FileName}
                onDelete={confirmDeleteFile}
            />

            {selectedEntity && fullResults.length > 0 && (
                <PiiDocumentModal
                    isOpen={isDocModalOpen}
                    onClose={onDocModalClose}
                    documents={fullResults.map(r => r.document)}
                    allEntities={fullResults.flatMap(r => r.entities)}
                    startPage={String(selectedEntity?.page)}
                />
            )}
        </Box>
    );
};

export default FilesPage;
